import React from "react";
import type { ForumRisk } from "../api/status";
import { getDisplayForum } from "../utils/cleaners";

interface ForumRiskTableProps {
  data: ForumRisk[];
}

function getRiskLevel(leaks: number): "critical" | "high" | "medium" | "low" {
  if (leaks > 20) return "critical";
  if (leaks > 10) return "high";
  if (leaks > 5) return "medium";
  return "low";
}

const RISK_BADGE: Record<string, { label: string; cls: string }> = {
  critical: { label: "Critical", cls: "frt-badge frt-badge-critical" },
  high: { label: "High", cls: "frt-badge frt-badge-high" },
  medium: { label: "Medium", cls: "frt-badge frt-badge-medium" },
  low: { label: "Low", cls: "frt-badge frt-badge-low" },
};

const ForumRiskTable: React.FC<ForumRiskTableProps> = ({ data }) => {
  return (
    <div className="frt-wrap">
      {/* Scrollable table area */}
      <div className="frt-scroll">
        <table className="frt-table">
          <thead>
            <tr className="frt-thead-row">
              <th className="frt-th">#</th>
              <th className="frt-th">Forum</th>
              <th className="frt-th">Recent Leaks (7d)</th>
              <th className="frt-th">Risk Level</th>
              <th className="frt-th">Growth</th>
              <th className="frt-th frt-th-hidden">Hidden %</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, idx) => {
              const risk = getRiskLevel(item.leaks_last_7d);
              const badge = RISK_BADGE[risk];
              const hiddenPct = Math.min(100, Math.max(0, item.hidden_pct ?? 0));

              return (
                <tr key={idx} className="frt-row">
                  <td className="frt-td frt-td-num">{idx + 1}</td>
                  <td className="frt-td frt-td-forum">
                    <span className="frt-forum-name">{getDisplayForum(item.forum, null)}</span>
                  </td>
                  <td className="frt-td frt-td-count">
                    {item.leaks_last_7d.toLocaleString()}
                  </td>
                  <td className="frt-td">
                    <span className={badge.cls}>{badge.label}</span>
                  </td>
                  <td className="frt-td">
                    {item.growth_pct != null ? (
                      <span
                        className="frt-growth"
                        style={{ color: item.growth_pct >= 0 ? "var(--color-success)" : "var(--color-danger)" }}
                      >
                        {item.growth_pct >= 0 ? "↑" : "↓"}{Math.abs(item.growth_pct).toFixed(1)}%
                      </span>
                    ) : (
                      <span className="frt-na" title="Growth data unavailable">—</span>
                    )}
                  </td>
                  <td className="frt-td frt-td-hidden">
                    <div className="frt-hidden-wrap">
                      <span className="frt-hidden-pct">{hiddenPct.toFixed(1)}%</span>
                      <div className="frt-bar-track">
                        <div className="frt-bar-fill" style={{ width: `${hiddenPct}%` }} />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bottom fade gradient hinting more rows */}
      {data.length > 6 && (
        <div className="frt-fade-out" aria-hidden="true" />
      )}
    </div>
  );
};

export default ForumRiskTable;
